// src/components/ToastContainer.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, Info, CheckCircle, X } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import type { ToastMessage } from '../types';
import './ToastContainer.css';

const Toast: React.FC<{ toast: ToastMessage }> = ({ toast }) => {
  const { dispatch } = useAppContext();
  const [isHiding, setIsHiding] = useState(false);

  // FIXED: Added useCallback to prevent handleDismiss from changing on every render
  const handleDismiss = useCallback(() => {
    setIsHiding(true);
    setTimeout(() => {
      dispatch({ type: 'REMOVE_TOAST', payload: toast.id });
    }, 300); // Animation duration
  }, [dispatch, toast.id]);

  // FIXED: Added handleDismiss to dependencies
  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, toast.duration || 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.duration, handleDismiss]); // FIXED: Added handleDismiss

  const { Icon, iconClass } = useMemo(() => {
    switch (toast.type) {
      case 'success':
        return { Icon: CheckCircle, iconClass: 'success' };
      case 'error':
        return { Icon: AlertTriangle, iconClass: 'error' };
      case 'warning':
        return { Icon: AlertTriangle, iconClass: 'warning' };
      case 'info':
      default:
        return { Icon: Info, iconClass: 'info' };
    }
  }, [toast.type]);

  return (
    <div className={`toast-item type-${toast.type} ${isHiding ? 'hiding' : ''}`}>
      <div className="toast-icon-container">
        <Icon className={`toast-icon ${iconClass}`} size={20} />
      </div>
      <div className="toast-content">
        <h4 className="toast-title">{toast.title}</h4>
        <p className="toast-message">{toast.message}</p>
      </div>
      <button onClick={handleDismiss} className="toast-dismiss-button" aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const { state } = useAppContext();

  if (state.toasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container">
      {state.toasts.map(toast => (
        <Toast key={toast.id} toast={toast} />
      ))}
    </div>
  );
};

export default ToastContainer;